import api from '../api/axios';

export type BlogLocale = 'en' | 'ar';

export interface BlogPostPayload {
  title: string;
  slug: string;
  excerpt?: string | null;
  content?: string | null;
  seoTitle?: string | null;
  seoDescription?: string | null;
  seoKeywords?: string | null;
}

const buildData = (
  payload: BlogPostPayload,
  options: { published: boolean; coverId?: number | null },
) => {
  const data: Record<string, unknown> = {
    ...payload,
  };
  if ('coverId' in options && options.coverId !== undefined) {
    data.cover = options.coverId || null;
  }
  if (options.published) {
    data.publishedAt = new Date().toISOString();
  } else {
    data.publishedAt = null;
  }
  return data;
};

export const BlogPostService = {
  getAll: (locale: BlogLocale = 'en', extras?: Record<string, unknown>) =>
    api.get('/blog-posts', {
      params: {
        locale,
        'populate[cover]': true,
        'sort[0]': 'publishedAt:desc',
        'pagination[pageSize]': 100,
        publicationState: 'preview',
        ...extras,
      },
    }),

  getOne: (id: number | string, locale: BlogLocale) =>
    api.get(`/blog-posts/${id}`, {
      params: {
        locale,
        populate: '*',
        publicationState: 'preview',
      },
    }),

  create: (
    locale: BlogLocale,
    payload: BlogPostPayload,
    published: boolean,
    coverId?: number | null,
  ) =>
    api.post(
      `/blog-posts`,
      {
        data: buildData(payload, {
          published,
          ...(coverId !== undefined ? { coverId } : {}),
        }),
      },
      { params: { locale } },
    ),

  update: (
    id: number | string,
    locale: BlogLocale,
    payload: BlogPostPayload,
    published: boolean,
    cover?: number | null,
  ) =>
    api.put(
      `/blog-posts/${id}`,
      {
        data: buildData(payload, {
          published,
          ...(cover !== undefined ? { coverId: cover } : {}),
        }),
      },
      { params: { locale } },
    ),

  /** Attach a translated copy to an existing post (different locale entry). */
  createLocalization: (
    baseDocumentId: number | string,
    locale: BlogLocale,
    payload: BlogPostPayload,
    published: boolean,
  ) =>
    api.post(`/blog-posts/${baseDocumentId}/localizations`, {
      locale,
      ...buildData(payload, { published }),
    }),

  delete: (id: number | string) => api.delete(`/blog-posts/${id}`),
};

export default BlogPostService;
